import {
    addMonths,
    addQuarters,
    addWeeks,
    addYears,
    areIntervalsOverlapping,
    compareAsc,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    isAfter,
    isBefore,
    isWithinInterval,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
} from 'date-fns';
import { Heap } from '../../../util/heap';
import { CalendarEvent } from './Calendar';

/**
 * Time utilities for the calendar.
 */
export const timeFunctions = {
    year: { add: addYears, start: startOfYear, end: endOfYear },
    quarter: { add: addQuarters, start: startOfQuarter, end: endOfQuarter },
    month: { add: addMonths, start: startOfMonth, end: endOfMonth },
    week: { add: addWeeks, start: startOfWeek, end: endOfWeek },
};

/*
 * Calendar track utilities for manipulating events positioning.
 */

/**
 * A track keeps information about the positioning of an event or a set of events.
 */
export type CalendarTrack = {
    events: CalendarEvent[];
    resolution: Interval[];
    ticks: { start: number; end: number };
    open: { start: boolean; end: boolean };
    interval: Interval;
    level: number;
};

/**
 * Create tracks for each one of `events` with respect to the received `tickResolution` intervals.
 * Each track uses a unique level index to prevent collisions.
 *
 * @param events events to create tracks
 * @param resolution track ticks
 * @returns calendar tracks
 */
export const createTracks = (events: CalendarEvent[], resolution: Interval[]) => {
    const fullInterval = { start: resolution[0].start, end: resolution[resolution.length - 1].end };
    return events
        .filter(event => areIntervalsOverlapping(event.interval, fullInterval, { inclusive: true }))
        .map<CalendarTrack>((event, i) => {
            const ticks = {
                start: resolution.findIndex(interval => isWithinInterval(event.interval.start, interval)),
                end: resolution.findIndex(interval => isWithinInterval(event.interval.end, interval)),
            };
            const open = { start: ticks.start === -1, end: ticks.end === -1 };
            if (ticks.start === -1) ticks.start = 0;
            if (ticks.end === -1) ticks.end = resolution.length - 1;
            const interval = { start: resolution[ticks.start].start, end: resolution[ticks.end].end };
            return { events: [event], resolution, ticks, open, interval, level: i };
        });
};

/**
 * Reposition track levels to reuse the same levels without collisions, making them more compact.
 * This algorithm uses a heap to find the best available levels, and the fit the next track on that level.
 * If the track cannot fit in the best available level, a new level is created for it.
 * The final result will depend on the set track order defined by the `sort` property, by default, the tracks are sort
 * by `start` date precedence and by decreasing `end` date if the `start` dates are the same.
 *
 * time: `O(t*log(t))`
 *
 * @param tracks tracks to arrange
 * @param sort sort weights for `[start_date, end_date]`, can be used to change sort order
 * @returns the same `tracks` with positioning information and the number of levels used
 */
export const looseArrangeTracks = (tracks: CalendarTrack[], sort: [number, number] = [2, -1]) => {
    const indices = new Heap<[Date, number]>([], v => v[0]);
    const [s, e] = sort;
    let levels = 0;
    tracks
        .sort(({ interval: a }, { interval: b }) => compareAsc(a.start, b.start) * s + compareAsc(a.end, b.end) * e)
        .forEach(track => {
            const [bestDate, bestLevel] = indices.peek() ?? [new Date(0), levels];
            track.level = isAfter(track.interval.start, bestDate) ? (indices.poll(), bestLevel) : levels;
            levels = Math.max(levels, track.level + 1);
            indices.offer([track.interval.end as Date, track.level]);
        });

    return { tracks, levels };
};

/**
 * Reposition track levels to reuse the same levels without collisions, using the minimum number of levels possible.
 * This algorithm is slower than `looseArrangeTracks`, but produces a more compact result.
 *
 * time: `O(t**2)`
 *
 * @param tracks tracks to arrange
 * @returns the same `tracks` with positioning information and the number of levels used
 */
export const denseArrangeTracks = (tracks: CalendarTrack[]) => {
    const levels: CalendarTrack[][] = [];
    tracks
        .sort(({ interval: a }, { interval: b }) => compareAsc(a.start, b.start) * 2 + compareAsc(a.end, b.end))
        .forEach(track => {
            for (let i = 0; i < levels.length; i++) {
                const levelTracks = levels[i];
                for (let j = 0; j < levelTracks.length; j++) {
                    const current = levelTracks[j];
                    const next = levelTracks[j + 1];
                    if (!isAfter(track.interval.start, current.interval.end)) continue;
                    if (!!next && !isBefore(track.interval.end, next.interval.start)) continue;
                    levelTracks.splice(j + 1, 0, track);
                    track.level = i;
                    return;
                }
            }
            levels.push([track]);
            track.level = levels.length - 1;
        });
    return { tracks, levels: levels.length };
};

/**
 * Compress tracks to fit in a smaller level size.
 * The results depend on the initial track arrangement, so calling `denseArrangeTracks` is recommended.
 * Compressed tracks are always fragmented in sub-tracks of minimum size given the tracks `resolution`.
 *
 * @param tracks tracks to compress
 * @param resolution track ticks
 * @param maxLevel maximum levels allowed
 */
export const compressTracks = (tracks: CalendarTrack[], resolution: Interval[], maxLevel = 3) => {
    // TODO
};
